import { Controller } from "@hotwired/stimulus"

/**
 * Keyboard shortcuts for the support ticket interface.
 *
 * data-controller="support--keyboard-shortcuts"
 * data-support--keyboard-shortcuts-index-value="/support/tickets"
 *
 * Keys:
 *   j / k     — Navigate up/down in ticket list
 *   Enter     — Open selected ticket
 *   r         — Focus reply box
 *   e         — Edit current ticket
 *   /         — Focus search input
 *   Escape    — Close modal / deselect
 *   ?         — Toggle help modal
 */
export default class extends Controller {
  static targets = ["ticketRow", "searchInput", "replyBox", "helpModal"]
  static values = {
    index: String, // base path for ticket list
  }

  connect() {
    this.selectedIndex = -1
    this._handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this._handleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this._handleKeydown)
  }

  handleKeydown(event) {
    // Skip when typing in an input, textarea, or contenteditable
    const tag = event.target.tagName
    if (
      tag === "INPUT" ||
      tag === "TEXTAREA" ||
      tag === "SELECT" ||
      event.target.isContentEditable
    ) {
      // Only handle Escape within inputs
      if (event.key === "Escape") {
        event.target.blur()
      }
      return
    }

    switch (event.key) {
      case "j":
        this.navigateDown()
        break
      case "k":
        this.navigateUp()
        break
      case "Enter":
        this.openSelected()
        break
      case "r":
        event.preventDefault()
        this.focusReply()
        break
      case "e":
        this.editCurrent()
        break
      case "/":
        event.preventDefault()
        this.focusSearch()
        break
      case "Escape":
        this.deselect()
        break
      case "?":
        this.toggleHelp()
        break
    }
  }

  navigateDown() {
    if (!this.hasTicketRowTarget) return
    this.selectedIndex = Math.min(
      this.selectedIndex + 1,
      this.ticketRowTargets.length - 1,
    )
    this.highlightSelected()
  }

  navigateUp() {
    if (!this.hasTicketRowTarget) return
    this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
    this.highlightSelected()
  }

  highlightSelected() {
    this.ticketRowTargets.forEach((row, i) => {
      row.classList.toggle("ring-2", i === this.selectedIndex)
      row.classList.toggle("ring-blue-500", i === this.selectedIndex)
    })

    const selected = this.ticketRowTargets[this.selectedIndex]
    if (selected) {
      selected.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }
  }

  openSelected() {
    const selected = this.ticketRowTargets[this.selectedIndex]
    if (!selected) return

    const link = selected.querySelector("a[href]")
    if (link) link.click()
  }

  focusReply() {
    if (this.hasReplyBoxTarget) {
      const editable = this.replyBoxTarget.querySelector(
        "lexxy-editor [contenteditable]",
      )
      if (editable) editable.focus()
    }
  }

  editCurrent() {
    const editLink = document.querySelector('a[data-action="edit-ticket"]')
    if (editLink) editLink.click()
  }

  focusSearch() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.focus()
    }
  }

  deselect() {
    this.selectedIndex = -1
    this.ticketRowTargets.forEach((row) => {
      row.classList.remove("ring-2", "ring-blue-500")
    })

    // Close help modal if open
    if (this.hasHelpModalTarget) {
      this.helpModalTarget.classList.add("hidden")
    }
  }

  toggleHelp() {
    if (this.hasHelpModalTarget) {
      this.helpModalTarget.classList.toggle("hidden")
    }
  }
}
